import axios from 'axios';

// Auto-detect backend URL based on current environment
const getBackendURL = () => {
  // In production (custom domain), always use same-origin
  const hostname = window.location.hostname;
  if (hostname !== 'localhost' && !hostname.includes('preview.emergentagent.com')) {
    return window.location.origin;
  }
  
  // In development/preview, use env var if set
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  return window.location.origin;
};

const API_BASE_URL = getBackendURL();

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip retry for blob responses (PDF downloads)
    if (originalRequest.responseType === 'blob') {
      return Promise.reject(error);
    }

    // Skip retry for auth endpoints (login/register should show their own errors)
    const authPaths = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/google'];
    if (authPaths.some(p => originalRequest.url?.includes(p))) {
      return Promise.reject(error);
    }

    // Skip auto-redirect for PUBLIC routes (no login expected)
    const publicPathPrefixes = ['/pagar/', '/telemarketing/pago-'];
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPublicRoute = publicPathPrefixes.some(p => currentPath.startsWith(p));

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isPublicRoute) {
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/api/v1/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = data.access_token;
        sessionStorage.setItem('access_token', newToken);
        
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        processQueue(null, newToken);
        isRefreshing = false;
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        
        sessionStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function for downloading PDFs without interceptor interference
export const downloadPDF = async (url, filename) => {
  const token = sessionStorage.getItem('access_token');
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  const response = await fetch(fullUrl, {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    credentials: 'include'
  });
  
  // Check if response is successful
  if (response.ok) {
    // Success - download the PDF
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    return;
  }
  
  // Error response - try to parse as JSON
  let errorData = null;
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    try {
      // Read response text first, then parse
      const text = await response.text();
      errorData = JSON.parse(text);
      console.log('Successfully parsed error data:', errorData);
    } catch (e) {
      console.log('Failed to parse JSON error:', e.message);
    }
  }
  
  // Create error object with response structure similar to axios
  const error = new Error(`HTTP error! status: ${response.status}`);
  error.response = {
    status: response.status,
    data: errorData
  };
  
  throw error;
};

export default api;
